import { useEffect, useState } from "react";
import { IconChartBar } from "@tabler/icons-react";
import Card from "./Card";
import { getPercent } from "../../../../utils/functions";
import styles from "../TalentProfile.module.css";

// Positional mapping preserved from the old AiInsightsVisual component.
// Talent side reads from talentDetails.abilities (string values, e.g. "83").
// AI side reads from talentDetails.psychometricTestResult (NOT aiInsights,
// which is a separate strengths/weaknesses/score summary object).
const SCORE_CONFIG = [
  {
    name: "Stress tolerance",
    abilityKey: "pressure",
    ai: (r) => r?.personality?.stressTolerance,
  },
  {
    name: "Collaboration",
    abilityKey: "collaboration",
    ai: (r) => r?.personality?.collaboration,
  },
  {
    name: "Judgement",
    abilityKey: "teamManagement",
    ai: (r) => r?.sjtScores?.judgment,
  },
  {
    name: "Teamwork",
    abilityKey: "teamWork",
    ai: (r) => r?.sjtScores?.teamwork,
  },
  {
    name: "Responsibility",
    abilityKey: "sales",
    ai: (r) => r?.sjtScores?.responsibility,
  },
  {
    name: "Adaptability",
    abilityKey: "planning",
    ai: (r) => r?.sjtScores?.adaptability,
  },
  {
    name: "Clarity",
    abilityKey: "clientRelationship",
    ai: (r) => r?.creativeScore?.clarity,
  },
  {
    name: "Creativity",
    abilityKey: "creativity",
    ai: (r) => r?.creativeScore?.creativity,
  },
  {
    name: "Communication",
    abilityKey: "communication",
    ai: (r) => r?.creativeScore?.communication,
  },
];

/**
 * Maps a talentDetails API object into the `scores` array shape
 * ScoreComparisonCard / ScoreComparisonBars expects: [{ name, talent, ai }, ...]
 */
export function mapToScoreComparisonData(talentDetails = {}) {
  const {
    abilities = {},
    psychometricTestResult = {},
    submittedPsychometricTest,
  } = talentDetails;

  return SCORE_CONFIG.map(({ name, abilityKey, ai }) => ({
    name,
    talent: getPercent(abilities?.[abilityKey], 100) || 0,
    ai: submittedPsychometricTest
      ? getPercent(ai(psychometricTestResult), 100) || 0
      : 0,
  }));
}

export function ScoreComparisonBars({ scores, animated }) {
  return (
    <>
      <div className={styles.scoreLegend}>
        <span className={styles.scoreLegendItem}>
          <span
            className={`${styles.scoreLegendDot} ${styles.scoreLegendDotTalent}`}
          />{" "}
          Talent Score
        </span>
        <span className={styles.scoreLegendItem}>
          <span
            className={`${styles.scoreLegendDot} ${styles.scoreLegendDotAi}`}
          />{" "}
          AI Score
        </span>
      </div>

      {scores?.map((s) => (
        <div className={styles.scoreTraitRow} key={s?.name}>
          <div className={styles.scoreTraitName}>{s?.name}</div>
          <div className={styles.scoreBarsWrap}>
            <div
              className={`${styles.scoreBarCol} ${styles.scoreBarColTalent}`}
            >
              <div className={`${styles.scoreVal} ${styles.scoreValTalent}`}>
                {s?.talent}
              </div>
              <div
                className={`${styles.scoreBarTrack} ${styles.scoreBarTrackTalent}`}
              >
                <div
                  className={`${styles.scoreBarFill} ${styles.scoreBarFillTalent}`}
                  style={{ width: animated ? `${s?.talent}%` : 0 }}
                />
              </div>
            </div>
            <div className={`${styles.scoreBarCol} ${styles.scoreBarColAi}`}>
              <div className={styles.scoreBarTrack}>
                <div
                  className={`${styles.scoreBarFill} ${styles.scoreBarFillAi}`}
                  style={{ width: animated ? `${s?.ai}%` : 0 }}
                />
              </div>
              <div className={`${styles.scoreVal} ${styles.scoreValAi}`}>
                {s?.ai}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

const ScoreComparisonCard = ({ scores }) => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <Card icon={IconChartBar} title="Score Comparison">
      <ScoreComparisonBars scores={scores} animated={animated} />
    </Card>
  );
};

export default ScoreComparisonCard;
